import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api.js';

const EMPTY = {
  stakeholders: [],
  resources: [],
  paymentMilestones: [],
  interactions: [],
  assignedUsers: [],
  memory: {
    currentStatus: [],
    playbook: [],
    updatedAt: null,
    steps: [],
    lifecycleMilestones: [],
  },
};

// Loads the per-project Intelligence payload (Info / History / Memory data) for
// the project detail page. Kept out of `/bootstrap` on purpose — this is heavy,
// per-project, and only needed once a project is open.
//
// `loaded` is false until the first successful fetch: the Info / History /
// Memory editors must not run until then, or a replace-all save from the empty
// fallback would wipe rows that just hadn't arrived yet.
export function useProjectDetail(projectId) {
  const [detail, setDetail] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  // Monotonic id of the newest fetch. A slower earlier GET that resolves after
  // a newer one must not apply its (now stale) snapshot.
  const gen = useRef(0);

  const apply = (data) =>
    setDetail({
      ...EMPTY,
      ...data,
      memory: { ...EMPTY.memory, ...data.memory },
    });

  const load = useCallback(
    async ({ rethrow }) => {
      const mine = (gen.current += 1);
      setLoading(true);
      try {
        const data = await api(`/projects/${projectId}/detail`);
        if (mine !== gen.current) return;
        apply(data);
        setLoaded(true);
        setError('');
      } catch (err) {
        if (mine === gen.current) setError(err.message);
        // A refresh after a successful mutation must not look like success —
        // the caller (EditableList etc.) needs to keep its editor open and
        // show the error rather than leave a stale snapshot on screen.
        if (rethrow) throw err;
      } finally {
        if (mine === gen.current) setLoading(false);
      }
    },
    [projectId],
  );

  const reload = useCallback(() => load({ rethrow: true }), [load]);

  useEffect(() => {
    setDetail(EMPTY);
    setLoaded(false);
    load({ rethrow: false }).catch(() => {});
    return () => {
      // Invalidate any in-flight fetch for the project we're leaving.
      gen.current += 1;
    };
  }, [projectId, load]);

  return { detail, loading, loaded, error, reload };
}
